import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Filter,
  ArrowLeft,
  Percent,
  Wallet,
  CalendarDays,
  Loader2,
  AlertCircle,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AnalyticsFreshnessBanner } from "@/components/admin/shared/AnalyticsFreshnessBanner";
import { analyticsApi } from "@/lib/admin-api";
import type { ConversionFunnelStage } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// Period options
// ---------------------------------------------------------------------------

const PERIOD_OPTIONS = [
  { label: "7 jours", value: 7 },
  { label: "30 jours", value: 30 },
  { label: "90 jours", value: 90 },
] as const;

// ---------------------------------------------------------------------------
// Stage display config
// ---------------------------------------------------------------------------

const STAGE_LABELS: Record<string, string> = {
  page_view: "Visiteurs",
  product_view: "Vue produit",
  add_to_cart: "Ajout panier",
  checkout_start: "Checkout",
  payment_initiated: "Paiement",
  purchase: "Commande",
};

const STAGE_COLORS: Record<string, string> = {
  page_view: "bg-blue-500",
  product_view: "bg-cyan-500",
  add_to_cart: "bg-amber-500",
  checkout_start: "bg-orange-500",
  payment_initiated: "bg-purple-500",
  purchase: "bg-emerald-500",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TopCard({
  label,
  value,
  icon: Icon,
}: {
  readonly label: string;
  readonly value: string;
  readonly icon: React.ElementType;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
        <Icon size={16} />
        <span>{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function FunnelBar({
  stage,
  maxCount,
  nextStage,
}: {
  readonly stage: ConversionFunnelStage;
  readonly maxCount: number;
  readonly nextStage?: ConversionFunnelStage;
}) {
  const widthPct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
  const colorClass = STAGE_COLORS[stage.name] ?? "bg-primary";
  const label = STAGE_LABELS[stage.name] ?? stage.name;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {stage.count.toLocaleString("fr-FR")}
        </span>
      </div>

      <div className="relative h-10 w-full rounded-lg bg-muted/30 overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 rounded-lg ${colorClass} transition-all duration-500`}
          style={{ width: `${Math.max(widthPct, 2)}%` }}
        />
        {/* Rate badge on the bar */}
        {stage.rate_from_previous < 1 && stage.rate_from_previous > 0 && (
          <div className="absolute inset-y-0 right-3 flex items-center">
            <span className="text-xs font-medium text-muted-foreground tabular-nums">
              {formatRate(stage.rate_from_previous)} du précédent
            </span>
          </div>
        )}
      </div>

      {/* Drop-off indicator between stages */}
      {nextStage && stage.dropoff > 0 && (
        <div className="flex items-center justify-end gap-1 text-xs text-red-400">
          <span className="tabular-nums">
            -{stage.dropoff.toLocaleString("fr-FR")} abandon
            ({formatPct(1 - (nextStage.count / (stage.count || 1)))})
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Funnel() {
  const [days, setDays] = useState<number>(7);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["analytics", "funnel", days],
    queryFn: () => analyticsApi.conversionFunnel(days),
  });

  // Build the payment split string
  const paymentSplitLabel = (() => {
    if (!data?.payment_split) return "\u2014";
    const { wave, orange_money, cod } = data.payment_split;
    const parts: string[] = [];
    if (wave > 0) parts.push(`Wave ${formatPct(wave)}`);
    if (orange_money > 0) parts.push(`Orange Money ${formatPct(orange_money)}`);
    if (cod > 0) parts.push(`Esp\u00e8ces ${formatPct(cod)}`);
    return parts.length > 0 ? parts.join(" \u00b7 ") : "\u2014";
  })();

  return (
    <AdminLayout>
      <div className="space-y-6">
        <AnalyticsFreshnessBanner />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/analytics"
              className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground">
                Entonnoir de conversion
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Analyse du parcours client et des taux de conversion
              </p>
            </div>
          </div>

          {/* Period selector */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  days === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-muted-foreground" size={28} />
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center">
            <AlertCircle className="mx-auto mb-2 text-destructive" size={24} />
            <p className="text-sm text-destructive">
              Impossible de charger l'entonnoir de conversion.
            </p>
          </div>
        )}

        {data && (
          <>
            {/* Top cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <TopCard
                label="Taux de conversion global"
                value={formatRate(data.overall_rate)}
                icon={Percent}
              />
              <TopCard
                label="Répartition paiements"
                value={paymentSplitLabel}
                icon={Wallet}
              />
              <TopCard
                label="Période"
                value={data.period ?? `${days} jours`}
                icon={CalendarDays}
              />
            </div>

            {/* Funnel visualization */}
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold text-foreground mb-6">
                Parcours de conversion
              </h2>

              {data.stages.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune donnée pour cette période.
                </p>
              ) : (
                <div className="space-y-5">
                  {data.stages.map((stage, idx) => (
                    <FunnelBar
                      key={stage.name}
                      stage={stage}
                      maxCount={data.stages[0]?.count ?? 1}
                      nextStage={
                        idx < data.stages.length - 1
                          ? data.stages[idx + 1]
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
